/*
 * user_ask 埋点辅助 — 把题目/答案压缩成神策可上报的紧凑字段
 *
 * 协议参考: ai_form_filler_user_ask_tracking.md
 *
 * 答案摘要不脱敏 (按数据团队要求),带原始 value/文本/picker summary。
 * 仅做分隔符转义 (|=:) 与超长截断 (1024 字符兜底)。
 */
import type { UserAskQuestion } from '../types'

/** ask_show.string3: ask title 截断 50 字符 */
export function truncateTitle(title: string | undefined | null): string {
  if (!title) return ''
  const t = String(title)
  return t.length <= 50 ? t : t.slice(0, 50)
}

/** ask_show.string2: 题型组合,按问题顺序 `|` 拼接.
 *  picker 题统一展开成 members / watermarks 两种 (按 picker.kind),其它题原样 type. */
export function buildTypeCombo(questions: UserAskQuestion[]): string {
  if (!Array.isArray(questions) || questions.length === 0) return ''
  return questions
    .map((q) => {
      if (q.type === 'picker') {
        return q.picker?.kind === 'watermark' ? 'watermarks' : 'members'
      }
      return q.type || ''
    })
    .join('|')
}

/** ask_show.bool1: 是否任一题带默认答案 */
export function hasAnyDefault(questions: UserAskQuestion[]): boolean {
  if (!Array.isArray(questions)) return false
  return questions.some((q) => q.default !== undefined && q.default !== null)
}

/** 判定单题"用户改过":
 *   - 题被 omit (answers 无此 qid) → 未改
 *   - 答案是显式跳过占位 → 未改
 *   - 跟 question.default 严格不等 (深比较) → 改过
 *  picker 题: default 通常缺,有 ids 数组就视作改过. */
function questionChanged(
  q: UserAskQuestion,
  answers: Record<string, any>
): boolean {
  if (!answers || !(q.qid in answers)) return false
  const v = answers[q.qid]
  if (v === undefined || v === null) return false
  if (typeof v === 'string' && v === '用户跳过选择') return false
  if (q.default === undefined || q.default === null) return true
  try {
    return JSON.stringify(v) !== JSON.stringify(q.default)
  } catch {
    return v !== q.default
  }
}

/** ask_submit.number2: 已改题数 */
export function countChangedQuestions(
  questions: UserAskQuestion[],
  answers: Record<string, any>
): number {
  if (!Array.isArray(questions)) return 0
  return questions.reduce(
    (n, q) => n + (questionChanged(q, answers) ? 1 : 0),
    0
  )
}

/** ask_submit.bool1: 是否所有题都改过 */
export function allQuestionsChanged(
  questions: UserAskQuestion[],
  answers: Record<string, any>
): boolean {
  if (!Array.isArray(questions) || questions.length === 0) return false
  return questions.every((q) => questionChanged(q, answers))
}

/** ask_submit.string2: 紧凑答案摘要 (带原始值,不脱敏).
 *  每题 `qid=type:value`, 多题 `|` 分隔. value 编码见文档第 3 节;
 *  value 内的分隔符字符 (|=:,) 统一替换成空格,避免破坏结构. */
export function buildAnswerSummary(
  questions: UserAskQuestion[],
  answers: Record<string, any>
): string {
  if (!Array.isArray(questions) || questions.length === 0) return ''
  const MAX_LEN = 1024
  const parts: string[] = []
  for (const q of questions) {
    const raw = answers?.[q.qid]
    const seg = encodeOneAnswer(q, raw)
    if (seg) parts.push(seg)
  }
  const joined = parts.join('|')
  if (joined.length <= MAX_LEN) return joined
  return joined.slice(0, MAX_LEN - '...truncated'.length) + '...truncated'
}

function encodeOneAnswer(q: UserAskQuestion, value: any): string {
  const qid = q.qid || ''
  if (!qid) return ''
  if (value === undefined || value === null) {
    return `${qid}=${typeTag(q)}:none`
  }
  if (typeof value === 'string' && value === '用户跳过选择') {
    return `${qid}=${typeTag(q)}:skipped`
  }
  switch (q.type) {
    case 'single_choice':
    case 'keyword_select':
    case 'confirm': {
      const v = Array.isArray(value) ? value[0] : value
      return `${qid}=${q.type}:${esc(String(v))}`
    }
    case 'multi_choice': {
      const arr = Array.isArray(value) ? value : [value]
      return `${qid}=multi_choice:${arr.map((v) => esc(String(v))).join(',')}`
    }
    case 'date_range': {
      // {start, end} 或 [start, end];完整 ISO 日期串
      const { s, e } = pickStartEnd(value)
      return `${qid}=date_range:${formatDate(s)}_${formatDate(e)}`
    }
    case 'text': {
      const s = typeof value === 'string' ? value : ''
      return `${qid}=text:${esc(s)}`
    }
    case 'person_select': {
      const arr = Array.isArray(value) ? value : []
      // 人员列表常是 id 串,直接用 , 拼;非字符串元素 stringify
      const parts = arr.map((it) =>
        esc(typeof it === 'string' ? it : JSON.stringify(it))
      )
      return `${qid}=person_select:${parts.join(',')}`
    }
    case 'picker': {
      const kind = q.picker?.kind === 'watermark' ? 'watermarks' : 'members'
      // PickerAnswerValue.summary 是人类可读摘要 (含部门 / 成员 / 水印名),直接用
      const summary = value && typeof value === 'object' ? value.summary : ''
      return `${qid}=${kind}:${esc(String(summary || ''))}`
    }
    default:
      return `${qid}=${q.type || 'unknown'}:${esc(safeStringify(value))}`
  }
}

function typeTag(q: UserAskQuestion): string {
  if (q.type === 'picker') {
    return q.picker?.kind === 'watermark' ? 'watermarks' : 'members'
  }
  return q.type || 'unknown'
}

/** 把会破坏分隔结构的字符 (|=:,) 替换成空格,保留可读性 */
function esc(s: string): string {
  if (!s) return ''
  return s.replace(/[|=:,]/g, ' ')
}

function safeStringify(v: any): string {
  try {
    return JSON.stringify(v)
  } catch {
    return String(v)
  }
}

function pickStartEnd(value: any): { s: any; e: any } {
  if (Array.isArray(value) && value.length >= 2) {
    return { s: value[0], e: value[1] }
  }
  if (value && typeof value === 'object') {
    return { s: value.start ?? value.startDate, e: value.end ?? value.endDate }
  }
  return { s: '', e: '' }
}

/** 把日期转 YYYY-MM-DD;解析失败保留原串. */
function formatDate(d: any): string {
  if (!d) return ''
  const date = new Date(d)
  if (Number.isNaN(date.getTime())) return esc(String(d))
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )}`
}

function pad2(n: any): string {
  const s = String(n)
  return s.length >= 2 ? s.slice(-2) : '0' + s
}
